import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, assetUrl } from "@/lib/api";
import { ExploreHeader, TodayHours, WeeklyHours, RestrictionBadges, Field } from "@/attendee/ExploreShared";
import { UtensilsCrossed, Wine } from "lucide-react";

export default function ExploreVenueDetail() {
  const { id } = useParams();
  const { eventId } = useApp();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    setR(null); setErr(false);
    api.exploreDiningDetail(eventId, id).then((d) => { if (active) setR(d); }).catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [eventId, id]);

  if (err) return <div className="pt-20 px-4"><div className="att-card p-8 text-center text-sm text-cream-dim">This venue is no longer available.</div></div>;
  if (!r) return <div className="pt-20 px-4"><div className="h-48 att-card animate-pulse" /></div>;

  const Icon = r.subtype === "bar" ? Wine : UtensilsCrossed;
  const hasValidLink = r.external_url && /^https?:\/\//i.test(r.external_url);

  return (
    <div className="pt-16 px-4 pb-8">
      <ExploreHeader eyebrow={r.cuisine} title={r.name} icon={r.image_url ? undefined : Icon} />

      {r.image_url && (
        <img
          src={assetUrl(r.image_url)}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-44 w-full object-cover rounded-2xl mb-4"
        />
      )}

      {r.restrictions?.length > 0 && <div className="mb-3"><RestrictionBadges restrictions={r.restrictions} /></div>}

      {/* Today — practical info first */}
      {r.today && (
        <div className="att-card p-4 mb-3" data-testid="venue-today">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim mb-1">Today's hours · {r.today.day_label} · Hotel local time</div>
          {r.today.closed ? (
            <div className="text-sm text-[hsl(var(--pc-cream)/0.6)]">Closed today</div>
          ) : (
            <div className="space-y-1">
              {r.today.periods.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-cream-dim">{p.label || "Service"}</span>
                  <span className="text-cream font-medium">{p.is_24h ? "Open 24 hours" : `${p.open}–${p.close}${p.close_next_day ? " (next day)" : ""}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {r.description && <p className="text-sm text-cream-dim leading-relaxed mb-3">{r.description}</p>}

      <div className="mb-3"><WeeklyHours weekly={r.weekly} /></div>

      {/* Details */}
      <div className="att-card p-4">
        <Field label="Location" value={r.location} />
        <Field label="Dress code" value={r.dress_code} />
        <Field label="Reservations" value={r.reservation_note} />
        <Field label="Good to know" value={r.notes} />
        <Field label="Extension" value={r.extension} />
        {hasValidLink && (
          <div className="py-2">
            <a href={r.external_url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[hsl(var(--pc-gold))] underline">View menu</a>
          </div>
        )}
      </div>
    </div>
  );
}
